/**
 * CacheStats Component
 * Displays top cached queries table with details modal
 * Redesigned for clarity and non-technical accessibility
 */

import React, { useState } from 'react';
import { ChevronRight, Database, TrendingUp, X, Copy, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import { useDashboardStore } from '../store/dashboardStore';

interface TopQuery {
  queryHash: string;
  query: string;
  hitCount: number;
  totalTokens: number;
  costSaved: number;
  lastHit: string;
}

interface ModalQuery extends TopQuery {
  responsePreview?: string;
}

const formatRelativeTime = (timestamp: string) => {
  const now = Date.now();
  const then = new Date(timestamp).getTime();
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return new Date(timestamp).toLocaleDateString();
};

const formatTokens = (tokens: number) => {
  if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(1)}M`;
  if (tokens >= 1000) return `${(tokens / 1000).toFixed(1)}k`;
  return tokens.toString();
};

export const CacheStats: React.FC = () => {
  const [selectedQuery, setSelectedQuery] = useState<ModalQuery | null>(null);
  const topQueries = useDashboardStore((state) => state.topQueries);

  const handleRowClick = (query: TopQuery) => {
    setSelectedQuery({
      ...query,
      responsePreview: `This is a cached response for: "${query.query}". The system has returned this exact response ${query.hitCount} times, saving ${query.totalTokens} tokens and $${query.costSaved.toFixed(2)} in API costs.`,
    });
  };

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  return (
    <div className="flex flex-col h-full bg-transparent rounded-2xl overflow-hidden">
      {/* Section Header */}
      <div className="px-5 py-4 border-b border-white/5 bg-white/5 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center shadow-sm">
            <Database className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-white uppercase tracking-wider">Most Reused Answers</h2>
            <p className="text-xs text-slate-500 mt-1 font-medium">
              Questions the system has answered before — and cached for instant reuse
            </p>
          </div>
        </div>

        {topQueries.length > 0 && (
          <div className="mt-3 flex items-center gap-2 p-2.5 bg-violet-500/10 rounded-xl border border-violet-500/20">
            <Sparkles className="w-3.5 h-3.5 text-violet-400 flex-shrink-0" />
            <p className="text-[10px] text-violet-300 font-black uppercase tracking-widest">
              Showing the top {topQueries.length} most frequently cached questions
            </p>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {topQueries.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full px-6 text-center py-12">
            <div className="w-14 h-14 bg-white/5 rounded-2xl flex items-center justify-center mb-4 border border-white/5">
              <Database className="w-7 h-7 text-slate-700" />
            </div>
            <p className="text-white font-bold text-sm">No cached answers yet</p>
            <p className="text-slate-500 text-xs mt-2 leading-relaxed max-w-[200px]">
              Once agents start making requests, the most common answers will appear here.
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-black/20 border-b border-white/5 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  Question / Request
                </th>
                <th className="px-4 py-3 text-right text-[10px] font-black text-slate-500 uppercase tracking-widest w-20">
                  Times Used
                </th>
                <th className="px-4 py-3 text-right text-[10px] font-black text-slate-500 uppercase tracking-widest w-24">
                  Tokens Saved
                </th>
                <th className="px-4 py-3 text-right text-[10px] font-black text-slate-500 uppercase tracking-widest w-24">
                  Money Saved
                </th>
                <th className="px-4 py-3 w-6" />
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {topQueries.map((query, idx) => (
                <tr
                  key={query.queryHash}
                  className="hover:bg-violet-50/50 cursor-pointer transition-colors duration-150 group"
                  onClick={() => handleRowClick(query)}
                >
                  <td className="px-4 py-3.5">
                    <div className="flex items-start gap-2.5">
                      {/* Rank Badge */}
                      <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-black mt-0.5 ${
                        idx === 0 ? 'bg-amber-100 text-amber-600' :
                        idx === 1 ? 'bg-gray-100 text-gray-500' :
                        idx === 2 ? 'bg-orange-100 text-orange-500' :
                        'bg-gray-50 text-gray-400'
                      }`}>
                        {idx + 1}
                      </span>
                      <div className="min-w-0">
                        <div className="text-sm font-bold text-white truncate max-w-[220px]" title={query.query}>
                          {query.query.length > 52
                            ? query.query.substring(0, 49) + '...'
                            : query.query}
                        </div>
                        <div className="text-[10px] font-bold uppercase tracking-tighter text-slate-500 mt-1">{formatRelativeTime(query.lastHit)}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3.5 text-right">
                    <span className="inline-flex items-center gap-1 text-sm font-bold text-gray-800">
                      <TrendingUp className="w-3 h-3 text-indigo-400" />
                      {query.hitCount}×
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-right">
                    <span className="text-sm font-semibold text-gray-700">
                      {formatTokens(query.totalTokens)}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-right">
                    <span className="inline-block bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-black text-[10px] px-2 py-1 rounded-full uppercase tracking-tighter">
                      ${query.costSaved.toFixed(2)}
                    </span>
                  </td>
                  <td className="px-4 py-3.5">
                    <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-indigo-400 transition-colors" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Query Detail Modal */}
      {selectedQuery && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedQuery(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="px-6 py-5 border-b border-white/5 flex items-center justify-between bg-white/5">
              <div>
                <h2 className="text-base font-bold text-white uppercase tracking-wider">Cached Answer Detail</h2>
                <p className="text-xs text-slate-500 mt-1 font-medium">This question has been answered and stored</p>
              </div>
              <button
                onClick={() => setSelectedQuery(null)}
                className="p-1.5 hover:bg-white rounded-lg transition text-gray-400 hover:text-gray-700"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Question */}
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-2">
                  Original Question
                </label>
                <div className="p-3.5 bg-gray-50 rounded-xl border border-gray-200 text-gray-900 text-sm leading-relaxed">
                  {selectedQuery.query}
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-blue-50 rounded-xl p-3 text-center border border-blue-100">
                  <p className="text-xl font-black text-blue-700">{selectedQuery.hitCount}×</p>
                  <p className="text-xs text-blue-500 font-medium mt-0.5">Times reused</p>
                </div>
                <div className="bg-violet-50 rounded-xl p-3 text-center border border-violet-100">
                  <p className="text-xl font-black text-violet-700">{formatTokens(selectedQuery.totalTokens)}</p>
                  <p className="text-xs text-violet-500 font-medium mt-0.5">Tokens saved</p>
                </div>
                <div className="bg-emerald-50 rounded-xl p-3 text-center border border-emerald-100">
                  <p className="text-xl font-black text-emerald-700">${selectedQuery.costSaved.toFixed(2)}</p>
                  <p className="text-xs text-emerald-500 font-medium mt-0.5">Cost avoided</p>
                </div>
              </div>

              {/* Response Preview */}
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-2">
                  Cached Response Preview
                </label>
                <div className="p-3.5 bg-gray-50 rounded-xl border border-gray-200 text-gray-600 text-xs leading-relaxed max-h-28 overflow-y-auto">
                  {selectedQuery.responsePreview}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-1">
                <button
                  onClick={() => handleCopyToClipboard(selectedQuery.query)}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-xl font-semibold text-sm transition-colors"
                >
                  <Copy className="w-4 h-4" />
                  Copy Question
                </button>
                <button
                  onClick={() => setSelectedQuery(null)}
                  className="flex-1 px-4 py-2.5 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-xl font-semibold text-sm transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
